import React, { FC, useCallback, useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import {
  ApiPeriodicityMonthlyOccurrence,
  ApiPeriodicityYearlyOccurrence,
} from '@allbin/mobilix-api-client';
import Button from '../core/Button';
import IconButton from '../core/IconButton';
import { cn } from '../../helpers/classnames';
import { IconPlus, IconTrash } from '../../icons';
import { Dropdown } from '../core/Dropdown';
import ToggleButtonGroup, {
  ToggleButtonOption,
} from '../core/ToggleButtonGroup';

interface PeriodicityControlProps {
  periodicity: Periodicity;
  onChange: (periodicity: Periodicity) => void;
  containerRef?: React.RefObject<HTMLElement>;
}

const messages = defineMessages({
  jan: {
    id: 'january',
    defaultMessage: 'January',
  },
  feb: {
    id: 'february',
    defaultMessage: 'February',
  },
  mar: {
    id: 'march',
    defaultMessage: 'March',
  },
  apr: {
    id: 'april',
    defaultMessage: 'April',
  },
  may: {
    id: 'may',
    defaultMessage: 'May',
  },
  jun: {
    id: 'june',
    defaultMessage: 'June',
  },
  jul: {
    id: 'july',
    defaultMessage: 'July',
  },
  aug: {
    id: 'august',
    defaultMessage: 'August',
  },
  sep: {
    id: 'september',
    defaultMessage: 'September',
  },
  oct: {
    id: 'october',
    defaultMessage: 'October',
  },
  nov: {
    id: 'november',
    defaultMessage: 'November',
  },
  dec: {
    id: 'december',
    defaultMessage: 'December',
  },
  yearly: {
    id: 'check_in_plan_management.yearly',
    defaultMessage: 'Yearly',
  },
  monthly: {
    id: 'check_in_plan_management.monthly',
    defaultMessage: 'Monthly',
  },
  remove: {
    id: 'remove',
    defaultMessage: 'Remove',
  },
  periodicity: {
    id: 'check_in_plan_management.periodicity_input_label',
    defaultMessage: 'Periodicity',
  },
  date: {
    id: 'check_in_plan_management.date',
    defaultMessage: 'Date',
  },
  month: {
    id: 'check_in_plan_management.month',
    defaultMessage: 'Month',
  },
});

const dateOptions = Array(31)
  .fill(null)
  .map((v, i) => {
    const date = (i + 1).toString();
    return {
      id: date,
      label: date,
    };
  });

const PeriodicityControl: FC<PeriodicityControlProps> = ({
  periodicity,
  onChange,
  containerRef,
}) => {
  const intl = useIntl();
  const { type, occurrences } = periodicity;

  const periodicityOptions = useMemo<ToggleButtonOption[]>(
    () => [
      {
        value: 'yearly',
        label: intl.formatMessage(messages.yearly),
      },
      {
        value: 'monthly',
        label: intl.formatMessage(messages.monthly),
      },
    ],
    [intl],
  );
  const monthOptions = useMemo(
    () => [
      {
        id: '1',
        label: intl.formatMessage(messages.jan),
      },
      {
        id: '2',
        label: intl.formatMessage(messages.feb),
      },
      {
        id: '3',
        label: intl.formatMessage(messages.mar),
      },
      {
        id: '4',
        label: intl.formatMessage(messages.apr),
      },
      {
        id: '5',
        label: intl.formatMessage(messages.may),
      },
      {
        id: '6',
        label: intl.formatMessage(messages.jun),
      },
      {
        id: '7',
        label: intl.formatMessage(messages.jul),
      },
      {
        id: '8',
        label: intl.formatMessage(messages.aug),
      },
      {
        id: '9',
        label: intl.formatMessage(messages.sep),
      },
      {
        id: '10',
        label: intl.formatMessage(messages.oct),
      },
      {
        id: '11',
        label: intl.formatMessage(messages.nov),
      },
      {
        id: '12',
        label: intl.formatMessage(messages.dec),
      },
    ],
    [intl],
  );

  const onPeriodicityTypeChange = useCallback(
    (type: 'yearly' | 'monthly') => {
      if (type !== periodicity.type) {
        if (type === 'yearly') {
          onChange({
            type,
            occurrences: [{ month: 1, date: 1 }],
          });
        } else {
          onChange({
            type,
            occurrences: [{ date: 1 }],
          });
        }
      }
    },
    [onChange, periodicity.type],
  );

  const onYearlyOccChange = useCallback(
    (occIndex: number, date: number, month: number) => {
      const clone = [...occurrences] as ApiPeriodicityYearlyOccurrence[];
      if (clone.length > occIndex) {
        clone.splice(occIndex, 1, { date, month });
      } else {
        clone.push({ date, month });
      }
      onChange({
        type: 'yearly',
        occurrences: clone,
      });
    },
    [occurrences, onChange],
  );
  const onMonthlyOccChange = useCallback(
    (occIndex: number, date: number) => {
      const clone = [...occurrences] as ApiPeriodicityMonthlyOccurrence[];
      if (clone.length > occIndex) {
        clone.splice(occIndex, 1, { date });
      } else {
        clone.push({ date });
      }
      onChange({
        type: 'monthly',
        occurrences: clone,
      });
    },
    [occurrences, onChange],
  );
  const onNewOccurrence = useCallback(() => {
    if (type === 'yearly') {
      onYearlyOccChange(9999, 1, 1);
      return;
    }
    onMonthlyOccChange(9999, 1);
  }, [onMonthlyOccChange, onYearlyOccChange, type]);

  return (
    <div className="w-full border-l border-workorder-300 pl-2">
      <p className="mt-2 text-[10px] uppercase leading-4 tracking-[1.5px]">
        <FormattedMessage
          id="check_in_plan_management.periodicity_input_label"
          defaultMessage="Periodicity"
        />
      </p>
      <ToggleButtonGroup
        id="periodicity-type"
        value={type}
        onChange={(value) =>
          onPeriodicityTypeChange(value as 'yearly' | 'monthly')
        }
        options={periodicityOptions}
        className="mb-2 w-full"
      />
      {type === 'yearly' &&
        occurrences.map((occ, i) => (
          <div
            key={i + '_' + occ.month + '_' + occ.date}
            className="flex flex-row items-end gap-2 pb-2 pt-6"
          >
            <Dropdown
              id={`yearly-date-${i}`}
              label={intl.formatMessage(messages.date)}
              placeholder={intl.formatMessage(messages.date)}
              options={dateOptions}
              value={dateOptions.find((o) => o.id === occ.date.toString())}
              onValueChange={(option) => {
                onYearlyOccChange(i, parseInt(option.id, 10), occ.month);
              }}
              containerRef={containerRef}
              preventReselect
            />
            <Dropdown
              id={`yearly-month-${i}`}
              label={intl.formatMessage(messages.month)}
              placeholder={intl.formatMessage(messages.month)}
              options={monthOptions}
              value={monthOptions.find((o) => o.id === occ.month.toString())}
              onValueChange={(option) => {
                onYearlyOccChange(i, occ.date, parseInt(option.id, 10));
              }}
              containerRef={containerRef}
              preventReselect
            />
            <IconButton
              icon={<IconTrash />}
              variant="ghost"
              color="red"
              className={cn(i === 0 ? 'pointer-events-none opacity-0' : '')}
              disabled={i === 0}
              aria-label={intl.formatMessage(messages.remove)}
              onClick={() => {
                const clone = [
                  ...occurrences,
                ] as ApiPeriodicityYearlyOccurrence[];
                clone.splice(i, 1);
                onChange({
                  type: 'yearly',
                  occurrences: clone,
                });
              }}
            />
          </div>
        ))}
      {type === 'monthly' &&
        occurrences.map((occ, i) => (
          <div
            key={i + '_' + occ.date}
            className="flex flex-row items-end gap-2"
          >
            <div className="grow basis-0 pt-6">
              <Dropdown
                id={`monthly-date-${i}`}
                label={intl.formatMessage(messages.date)}
                placeholder={intl.formatMessage(messages.date)}
                options={dateOptions}
                value={dateOptions.find((o) => o.id === occ.date.toString())}
                onValueChange={(option) => {
                  onMonthlyOccChange(i, parseInt(option.id, 10));
                }}
                containerRef={containerRef}
                preventReselect
              />
            </div>
            <IconButton
              variant="ghost"
              color="red"
              icon={<IconTrash />}
              aria-label={intl.formatMessage(messages.remove)}
              onClick={() => {
                const clone = [
                  ...occurrences,
                ] as ApiPeriodicityMonthlyOccurrence[];
                clone.splice(i, 1);
                onChange({
                  type: 'monthly',
                  occurrences: clone,
                });
              }}
            />
          </div>
        ))}
      <Button
        variant="filled"
        size="sm"
        className="mt-2 w-full"
        startIcon={<IconPlus />}
        onClick={onNewOccurrence}
      >
        <FormattedMessage
          id="check_in_plan_management.new_occurrence"
          defaultMessage="New occurrence"
        />
      </Button>
    </div>
  );
};

export default PeriodicityControl;
